import type { KeyedArray } from "@repo/models";
import type { FC, ReactNode } from "react";

import { log, PushErrorNotification, PushSuccessNotification } from "@repo/helpers";
import { Button, ButtonTypes, Card, CardContent, CardFooter, Loading } from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { BackgroundCheck, DrugTest } from "../../models";

import { useBackgroundCheckApi } from "../../api/backgroundCheckApi";
import { useDrugTestApi } from "../../api/drugTestApi";
import * as Queries from "../../api/queries";
import { DrugTestResultEnum } from "../../models";
import style from "./style.module.scss";

interface AddSegmentWizardProps {
	newIds: number[];
	backgroundChecks: KeyedArray<BackgroundCheck>;
	backgroundChecksLoaded: boolean;
	drugTests: KeyedArray<DrugTest>;
	drugTestsLoaded: boolean;
}

export const AddSegmentWizard: FC<AddSegmentWizardProps> = ({
	newIds,
	backgroundChecks,
	backgroundChecksLoaded,
	drugTests,
	drugTestsLoaded,
}) => {
	const filteredBgc = backgroundChecks.filter((i) => !newIds.includes(i.onboardingSegmentId));
	const filteredDt = drugTests.filter((i) => !newIds.some((id) => i.onboardingSegmentsId.includes(id)));

	return (
		<section id="add-segment-wizard" className={style.segmentWizardContainer}>
			<p>This Agent has existing items from other onboarding segments. </p>
			<span>Please review the following items and select which you would like to:</span>
			<ul>
				<li>
					<b>Copy From: </b>
					Create a copy of the selected item and association it with the new Onboarding Segment
				</li>
				<li>
					<b>Associate With: </b>
					Update the selected item and add an association to the new Onboarding Segment
				</li>
			</ul>
			<div className={style.wizardGrid}>
				<SegmentWizardBackgroundCheck data={filteredBgc} loaded={backgroundChecksLoaded} segmentIds={newIds} />
				<SegmentWizardDrugTest data={filteredDt} loaded={drugTestsLoaded} segmentIds={newIds} />
			</div>
		</section>
	);
};

interface AddSegmentWizardComponentProps<T> {
	data: KeyedArray<T>;
	loaded: boolean;
	segmentIds: number[];
}

const SegmentWizardBackgroundCheck: FC<AddSegmentWizardComponentProps<BackgroundCheck>> = ({
	data,
	loaded,
	segmentIds,
}) => {
	const { copyToNewSegment } = useBackgroundCheckApi();
	const queryClient = useQueryClient();

	const handleCopy = async (id: number) => {
		for (const segmentId of segmentIds) {
			await copyToNewSegment(id, segmentId);
		}

		await queryClient.invalidateQueries({ queryKey: Queries.BACKGROUND_CHECKS });
		await queryClient.invalidateQueries({ queryKey: Queries.AGENT_PORTAL_TAB_COUNTS });
		await queryClient.invalidateQueries({ queryKey: [Queries.GROUP.REP] });

		return 0;
	};

	return (
		<SegmentWizardComponent<BackgroundCheck>
			data={data}
			loaded={loaded}
			copyFn={handleCopy}
			title="Background Checks"
			renderItem={SingleItem}
			segmentIds={segmentIds}
		/>
	);

	function SingleItem(backgroundCheck: BackgroundCheck, buttons: ReactNode) {
		return (
			<Card>
				<CardContent>
					<dl className={style.gridItem}>
						<dt>Request Date</dt>
						<dd>{backgroundCheck.requestDate?.toLocaleDateString()}</dd>
						<dt>Response Date</dt>
						<dd>{backgroundCheck.responseDate?.toLocaleDateString()}</dd>
						<dt>Provider</dt>
						<dd>{backgroundCheck.screeningProviderName}</dd>
						<dt>Segment</dt>
						<dd>{backgroundCheck.onboardingSegment.name}</dd>
					</dl>
				</CardContent>
				<CardFooter>{buttons}</CardFooter>
			</Card>
		);
	}
};

const SegmentWizardDrugTest: FC<AddSegmentWizardComponentProps<DrugTest>> = ({ data, loaded, segmentIds }) => {
	const { copyToNewSegment, associateToNewSegment } = useDrugTestApi();
	const queryClient = useQueryClient();

	const handleCopy = async (id: number) => {
		for (const segmentId of segmentIds) {
			await copyToNewSegment(id, segmentId);
		}

		await queryClient.invalidateQueries({ queryKey: Queries.DRUG_TESTS });
		await queryClient.invalidateQueries({ queryKey: Queries.AGENT_PORTAL_TAB_COUNTS });
		await queryClient.invalidateQueries({ queryKey: [Queries.GROUP.REP] });

		return 0;
	};

	const handleAssociate = async (id: number) => {
		for (const segmentId of segmentIds) {
			await associateToNewSegment(id, segmentId);
		}

		await queryClient.invalidateQueries({ queryKey: Queries.DRUG_TESTS });
		await queryClient.invalidateQueries({ queryKey: [Queries.GROUP.REP] });

		return 0;
	};

	return (
		<SegmentWizardComponent<DrugTest>
			data={data}
			loaded={loaded}
			title="Drug Tests"
			renderItem={SingleItem}
			copyFn={handleCopy}
			associateFn={handleAssociate}
			segmentIds={segmentIds}
		/>
	);

	function SingleItem(drugTest: DrugTest, buttons: ReactNode) {
		return (
			<Card>
				<CardContent>
					<dl className={style.gridItem}>
						<dt>Request Date</dt>
						<dd>{drugTest.conductedOn?.toLocaleDateString()}</dd>
						<dt>Provider</dt>
						<dd>{drugTest.providerName}</dd>
						<dt>Result</dt>
						<dd>{DrugTestResultEnum[drugTest.result]}</dd>
						<dt>Segments</dt>
						<dd>{drugTest.onboardingSegments.map((i) => i.name).join(" - ")}</dd>
					</dl>
				</CardContent>
				<CardFooter>{buttons}</CardFooter>
			</Card>
		);
	}
};

interface WizardComponentProps<T> {
	data: KeyedArray<T>;
	loaded: boolean;
	title: string;
	segmentIds: number[];
	renderItem: (item: T, buttons: ReactNode) => ReactNode;
	associateFn?: (id: number) => Promise<number>;
	copyFn: (id: number) => Promise<number>;
}

function SegmentWizardComponent<T>(props: WizardComponentProps<T>) {
	const { data, loaded, title, renderItem, associateFn, copyFn, segmentIds } = props;
	const [loadingCopyId, setLoadingCopyId] = useState<number | null>(null);
	const [associateId, setAssociateId] = useState<number | null>(null);

	const [processedIds, setProcessedIds] = useState<Set<number>>(new Set());

	if (loaded && !data.any()) return null;

	const handleCopyClick = async (id: number) => {
		setLoadingCopyId(id);
		try {
			await copyFn(id);
			setProcessedIds((prev) => new Set(prev).add(id));
			PushSuccessNotification("Successfully copied item");
		} catch (e) {
			log(e, "error");
			PushErrorNotification("Failed to copy item");
		} finally {
			setLoadingCopyId(null);
		}
	};

	const handleAssociateClick = async (id: number) => {
		setAssociateId(id);
		try {
			await associateFn(id);
			setProcessedIds((prev) => new Set(prev).add(id));
			PushSuccessNotification("Successfully associated item");
		} catch (e) {
			log(e, "error");
			PushErrorNotification("Failed to associate item");
		} finally {
			setAssociateId(null);
		}
	};

	return (
		<Loading loading={!loaded}>
			<section id={title}>
				<h3>{title}</h3>
				<div className={style.cardGrid}>{data?.map(renderSingleItem)}</div>
			</section>
		</Loading>
	);

	function renderSingleItem(item: T) {
		const id = data.keyFn(item) as number;

		const buttons = (
			<div className={style.flexRow}>
				<Button
					types={[ButtonTypes.Outline]}
					onClick={() => handleCopyClick(id)}
					loading={loadingCopyId === id}
					disabled={processedIds.has(id)}
				>
					{segmentIds.length > 1 ? "Copy to All" : "Copy From"}
				</Button>
				{associateFn && (
					<Button
						types={[ButtonTypes.Outline]}
						onClick={() => handleAssociateClick(id)}
						loading={associateId === id}
						disabled={processedIds.has(id)}
					>
						{segmentIds.length > 1 ? "Associate with All" : "Associate With"}
					</Button>
				)}
			</div>
		);

		return <div key={id}>{renderItem(item, buttons)}</div>;
	}
}
